import React, { useRef, useCallback } from 'react';
import { Camera, X, Navigation, Zap, ZapOff } from 'lucide-react';
import Webcam from 'react-webcam';
import { format } from 'date-fns';

interface CameraViewProps {
  isFullscreen: boolean;
  facingMode: 'user' | 'environment';
  cameraError: string | null;
  selectedTask: string;
  lastPhoto: { url: string; timestamp: number } | null;
  flashMode: 'auto' | 'on' | 'off';
  hasFlash: boolean;
  onCapture: () => void;
  onExit: () => void;
  onUserMedia: () => void;
  onError: (error: string | DOMException) => void;
  onFlashToggle: () => void;
  webcamRef: React.RefObject<Webcam>;
}

export function CameraView({
  isFullscreen,
  facingMode,
  cameraError,
  selectedTask,
  lastPhoto,
  flashMode,
  hasFlash,
  onCapture,
  onExit,
  onUserMedia,
  onError,
  onFlashToggle,
  webcamRef
}: CameraViewProps) {
  const videoConstraints = React.useMemo(() => ({
    width: { ideal: 1920 },
    height: { ideal: 1080 },
    facingMode: { exact: facingMode },
    aspectRatio: 16/9
  }), [facingMode]);

  return (
    <div 
      id="camera-container"
      className={`relative ${isFullscreen ? 'fixed inset-0 z-50 flex items-center justify-center bg-black' : 'aspect-video'} rounded-lg overflow-hidden mb-6 bg-black`}
    >
      {/* GPS Indicator */}
      <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
        <div className="flex items-center gap-1 bg-yellow-400 px-2 py-1 rounded">
          <span className="text-black font-medium text-sm">GPS</span>
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
        </div>
      </div>

      {/* Photo Counter */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-4">
        <div className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2">
          <span className="font-medium">{selectedTask}</span>
        </div>
      </div>

      <Webcam
        ref={webcamRef}
        audio={false}
        forceScreenshotSourceSize
        screenshotFormat="image/jpeg"
        videoConstraints={videoConstraints}
        onUserMediaError={onError}
        mirrored={false}
        imageSmoothing
        className={`${isFullscreen ? 'max-h-screen w-auto' : 'w-full h-full object-cover'} brightness-95`}
        style={{ 
          transform: isFullscreen ? 'none' : undefined
        }}
        onUserMedia={onUserMedia}
      />

      {cameraError && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-75 text-white p-4 text-center">
          <div>
            <p>{cameraError}</p>
          </div>
        </div>
      )}

      {/* Camera Controls */}
      <div className="absolute top-4 right-4 z-10 flex items-center gap-4">
        {/* Flash Toggle */}
        {hasFlash && (
          <button
            onClick={onFlashToggle}
            className="p-2 bg-black bg-opacity-50 rounded-full text-white hover:bg-opacity-75 transition-colors"
          >
            {flashMode === 'on' ? (
              <Zap className="h-6 w-6" />
            ) : flashMode === 'off' ? (
              <ZapOff className="h-6 w-6" />
            ) : (
              <span className="text-sm px-2">Auto</span>
            )}
          </button>
        )}

        {/* Exit Button */}
        <button
          onClick={onExit}
          className="p-2 bg-black bg-opacity-50 rounded-full text-white hover:bg-opacity-75 transition-colors"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      {/* Camera Controls */}
      <div className="absolute right-8 top-1/2 -translate-y-1/2 z-10">
        <button
          onClick={onCapture}
          className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
          disabled={!selectedTask || !!cameraError}
        >
          <Camera className="h-10 w-10 text-white" />
        </button>
      </div>
      
      {/* Last Photo Preview */}
      {lastPhoto && (
        <div className="absolute left-8 bottom-8 z-10">
          <div className="relative group">
            <img 
              src={lastPhoto.url} 
              alt="Last photo"
              className="w-28 h-28 object-cover rounded-lg border-2 border-white/50 shadow-lg"
            />
            <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
              <span className="text-white text-xs text-center">
                {format(lastPhoto.timestamp, 'HH:mm:ss')}
              </span>
            </div>
          </div>
        </div>
      )}
      
      {/* Flash Animation */}
      <div 
        id="flash" 
        className="absolute inset-0 bg-white opacity-0 pointer-events-none"
        style={{ animation: 'none' }}
      />
    </div>
  );
}